import useFeeTierModal from 'src/components/Modals/FeeTierModal/hooks/useFeeTierModal';
import { FEE_TIER_DISPLAY_UNIT_OPTIONS } from 'src/components/Modals/FeeTierModal/consts';
import FeeTierModalProgressBar from 'src/components/Modals/FeeTierModal/FeeTierModalProgressBar';
import FeeTierModalTable from 'src/components/Modals/FeeTierModal/FeeTierModalTable/FeeTierModalTable';
import FeeTierModalStakedTierSelect from 'src/components/Modals/FeeTierModal/FeeTierModalStakedTierSelect';
import DelegateWalletMessage from 'src/components/DelegateWalletMessage/DelegateWalletMessage';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import Divider from 'src/components/primitives/Divider/Divider';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';
import Env from 'src/environmentVariables/EnvironmentVariables';

export default function FeeTierModal() {
	const {
		feeTiers,
		userVolumeTierIndex,
		userCurrentVolume,
		userNextTierVolume,
		userCurrentStakedAmount,
		userCurrentStakedTier,
		selectedStakedTier,
		displayUnit,
		progressBarPercentage,
		tableRows,
		handleStakeClick,
		handleChangeDisplayUnit,
		handleChangeStakedTier,
		handleCloseModal,
		isUserOnHighestVolumeTier,
	} = useFeeTierModal();

	if (!feeTiers) {
		return null;
	}

	const userNextTierLabel = (() => {
		if (isUserOnHighestVolumeTier) {
			return null;
		}

		// If the user is one away from reaching the highest tier, we show "VIP".
		if (userVolumeTierIndex === feeTiers.length - 2) {
			return 'VIP';
		}

		return `Tier ${userVolumeTierIndex + 2}`;
	})();

	return (
		<ResponsiveModal.Container
			onClose={handleCloseModal}
			className="sm:w-min sm:max-w-[612px] sm:min-w-[396px]"
			dataTestId="fee-tier-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="fee-tier-modal-close">
				Fee table
			</ResponsiveModal.Title>
			<ResponsiveModal.Body className="max-h-none">
				<div className="flex flex-col gap-y-lg">
					<DelegateWalletMessage inherited="fee tier" />
					<div className="flex flex-col gap-y-xs">
						<div className="flex items-center justify-between typo-body-xs text-text-default">
							<span>30D volume (rolling est.)</span>
							{!isUserOnHighestVolumeTier && (
								<span>Next: {userNextTierLabel}</span>
							)}
						</div>
						<div className="flex items-center justify-between typo-heading-h4 text-text-default">
							<span>{userCurrentVolume}</span>
							{!isUserOnHighestVolumeTier && <span>{userNextTierVolume}</span>}
						</div>
					</div>
					<FeeTierModalProgressBar
						progressBarPercentage={progressBarPercentage}
					/>
					<span className="text-text-secondary">
						Your current booster is {userCurrentStakedTier.discount * 100}% with{' '}
						{userCurrentStakedAmount.toLocaleString(Env.locale, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}{' '}
						DRIFT staked.{' '}
						<TextButton size="sm" variant="tertiary" onClick={handleStakeClick}>
							Stake DRIFT here
						</TextButton>
					</span>
				</div>
				<Divider />
				<div className="flex flex-col gap-y-md">
					<div className="flex items-center justify-between">
						<span className="typo-body-xs text-text-default">Fee Tiers</span>
						<RadioGroup.Default
							size="md"
							variant="card"
							items={FEE_TIER_DISPLAY_UNIT_OPTIONS}
							value={displayUnit}
							onValueChange={handleChangeDisplayUnit}
						/>
					</div>
					<FeeTierModalStakedTierSelect
						selectedStakedTier={selectedStakedTier}
						userCurrentStakedTierDiscount={userCurrentStakedTier.discount}
						onSelect={handleChangeStakedTier}
					/>
					<FeeTierModalTable
						tableRows={tableRows}
						userVolumeTierIndex={userVolumeTierIndex}
						isUserOnHighestVolumeTier={isUserOnHighestVolumeTier}
					/>
					<span className="typo-body-2xs text-text-secondary">
						Progress to the next fee tier by satisfying the rolling 30D volume
						requirement. Stake more DRIFT to receive further discount on taker
						volume and more rebates on maker volume. Negative fees represent
						rebates. Spot trading volume also counts toward your total volume.
					</span>
				</div>
			</ResponsiveModal.Body>
			<ResponsiveModal.Footer className="sm:hidden">
				{({ close }) => (
					<Button
						size="lg"
						className="w-full"
						variant="primary"
						onClick={close}
						dataTestId="fee-tier-modal-close-button"
					>
						Close
					</Button>
				)}
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}
